import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { PRONTUARIO_NOTES_PADDING_X } from '../../constants/prontuarioTheme';
import { formatProntuarioSessionTitle } from '../../utils/formatProntuarioSessionTitle';
import {
	loadProntuarioSessionNotes,
	saveProntuarioSessionNotes,
} from '../../utils/prontuarioSessionNotesStorage';
import { SessionCallProntuarioNotesEditor } from './SessionCallProntuarioNotesEditor';
import { SessionCallProntuarioNotesFormattingHelp } from './SessionCallProntuarioNotesFormattingHelp';

type SessionCallProntuarioNotesSectionProps = {
	session?: Sessions.Session | null;
	className?: string;
	tutorialDemoTarget?: string;
};

const SessionCallProntuarioNotesSection = ({
	session,
	className,
	tutorialDemoTarget,
}: SessionCallProntuarioNotesSectionProps) => {
	const notesFieldId = useId();
	const notesRef = useRef<HTMLTextAreaElement>(null);
	const sessionId = session?.id ?? null;
	const selectedDate = session?.selected_date ?? null;
	const [notes, setNotes] = useState('');
	const title = formatProntuarioSessionTitle(session?.selected_date);

	useEffect(() => {
		if (!sessionId) {
			setNotes('');
			return;
		}

		setNotes(loadProntuarioSessionNotes(sessionId, selectedDate));
	}, [sessionId, selectedDate]);

	const handleNotesChange = useCallback(
		(value: string) => {
			setNotes(value);
			if (!sessionId) return;
			saveProntuarioSessionNotes(sessionId, value, selectedDate);
		},
		[selectedDate, sessionId],
	);

	const focusNotes = () => {
		notesRef.current?.focus();
	};

	return (
		<div
			{...(tutorialDemoTarget
				? { 'data-tutorial-demo': tutorialDemoTarget }
				: {})}
			className={cn('flex min-h-0 flex-1 flex-col', className)}
		>
			<div
				className={cn(
					'inline-flex w-fit shrink-0 items-center gap-1.5 pb-0.5 pt-3',
					PRONTUARIO_NOTES_PADDING_X,
				)}
			>
				<label
					htmlFor={notesFieldId}
					className={cn(
						'cursor-text',
						'text-[1.65rem] font-semibold leading-tight tracking-tight text-slate-100',
					)}
					aria-label={`${title} — ir para anotações`}
				>
					{title}
				</label>
				<SessionCallProntuarioNotesFormattingHelp />
			</div>

			<div
				className={cn(
					'flex min-h-0 flex-1 cursor-text flex-col pb-2',
					PRONTUARIO_NOTES_PADDING_X,
				)}
				onClick={focusNotes}
				onKeyDown={(event) => {
					if (event.target !== event.currentTarget) return;
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						focusNotes();
					}
				}}
				role="presentation"
			>
				<SessionCallProntuarioNotesEditor
					id={notesFieldId}
					textareaRef={notesRef}
					value={notes}
					onChange={handleNotesChange}
					placeholder="Você pode fazer suas anotações da sessão aqui e elas serão salvas automaticamente..."
					ariaLabel={`Anotações de ${title}`}
					className="min-h-0 w-full flex-1"
				/>
			</div>
		</div>
	);
};

export { SessionCallProntuarioNotesSection };
